// =============================================================================
// app/navigation.ts — Metadados de navegação canônicos (F4-S03 e futuros).
//
// Usado por: Sidebar, Topbar, breadcrumbs.
// Cada item declara href, label, icon key e permissão mínima (undefined = pública).
// =============================================================================

// ─── Regras de acesso (F58-S13) ───────────────────────────────────────────────
//
// Uma regra é uma permissão simples, "qualquer uma de" ({ any }) ou "todas de"
// ({ all }). É a MESMA regra que esconde o item do menu / o card de
// Configurações e que barra a rota (RequirePermission) — fonte única, para o
// menu e a URL digitada nunca divergirem. Isto é só apresentação: a API
// continua sendo quem autoriza de verdade.

export type AccessRule =
  | string
  | { readonly any: readonly string[] }
  | { readonly all: readonly string[] };

/** Avalia uma regra contra o predicado de permissão do usuário. Fail-closed em lista vazia. */
export function satisfiesAccess(
  rule: AccessRule,
  hasPermission: (permission: string) => boolean,
): boolean {
  if (typeof rule === 'string') return hasPermission(rule);
  if ('any' in rule) return rule.any.some((p) => hasPermission(p));
  return rule.all.length > 0 && rule.all.every((p) => hasPermission(p));
}

/** Acesso às telas de topo do app (menu lateral + rota). */
export const NAV_ACCESS = {
  /** Dashboard: visão geral (`read`) ou a do próprio agente (`read_by_agent`). */
  dashboard: { any: ['dashboard:read', 'dashboard:read_by_agent'] },
  crm: 'leads:read',
  /** Importar leads (wizard): a API exige `leads:write` em upload/confirm (imports/routes.ts). */
  leadImport: 'leads:write',
  creditAnalyses: 'credit_analyses:read',
  contracts: 'contracts:read',
  /** Inbox de conversas (live chat). */
  conversations: 'livechat:conversation:read',
  /** Painel "Minhas tarefas" (tasks/routes.ts). */
  tasks: 'tasks:read',
  /**
   * Relatórios: qualquer permissão que abre ao menos uma seção da página
   * (Atendimentos/Produtividade = dashboard:*, Crédito = billing:read,
   * Auditoria = audit:read, Comercial = sales_reports:read).
   */
  reports: {
    any: [
      'dashboard:read',
      'dashboard:read_by_agent',
      'billing:read',
      'audit:read',
      'sales_reports:read',
    ],
  },
  /** Simulador (D8): sem ver os produtos não há o que simular. */
  simulator: { all: ['credit_products:read', 'simulations:create'] },
} as const satisfies Record<string, AccessRule>;

/**
 * Acesso a cada tela de administração. Usado pelo card de ConfiguracoesPage
 * E pela rota correspondente em App.tsx — um único valor por tela.
 * A flag de cada tela (quando existe) segue sendo checada pelo card e pela página.
 */
export const ADMIN_ACCESS = {
  products: 'credit_products:read',
  productsWrite: 'credit_products:write',
  cities: 'cities:manage',
  agents: 'agents:manage',
  followupRules: 'followup:write',
  followupJobs: 'followup:read',
  billingDues: 'billing:read',
  billingRules: 'billing:write',
  billingJobs: 'billing:read',
  templates: 'templates:read',
  templatesWrite: 'templates:write',
  quickReplies: 'livechat:quick_reply:read',
  commercial: 'sales_campaigns:manage',
  lawFirms: 'law_firms:manage',
  channels: 'channel.connect',
  documentTypes: 'documents:manage',
  inboxAssignments: 'livechat:inbox_assignment:manage',
  aiPrompts: 'ai_prompts:read',
  aiDecisions: 'ai_decisions:read',
  aiPlayground: 'ai_playground:run',
  aiActions: 'ai_actions:read',
  users: 'users:manage',
  roles: 'users:assign_privileged_roles',
  featureFlags: 'flags:manage',
  tutorials: 'tutorials:manage',
  notifications: 'notifications:manage',
  policies: 'policies:manage',
} as const satisfies Record<string, AccessRule>;

export interface NavItem {
  href: string;
  label: string;
  /** Chave do ícone (resolução no componente Sidebar). */
  iconKey: string;
  /** Regra RBAC mínima. undefined = qualquer autenticado. */
  permission?: AccessRule | undefined;
  /** Se deve mostrar apenas quando feature flag ativa. */
  featureFlag?: string | undefined;
}

export interface NavSection {
  heading?: string | undefined;
  items: NavItem[];
}

/**
 * Navegação principal do app autenticado.
 * Sidebar e Topbar devem usar esta lista como fonte de verdade.
 */
export const APP_NAV: NavSection[] = [
  {
    items: [
      {
        href: '/',
        label: 'Dashboard',
        iconKey: 'dashboard',
        permission: NAV_ACCESS.dashboard,
      },
    ],
  },
  {
    heading: 'Operações',
    items: [
      { href: '/crm', label: 'CRM', iconKey: 'crm', permission: NAV_ACCESS.crm },
      // F33-S05 (doc 26 §5): item aparece só com sales_appointments:read E a
      // flag do piloto comercial. iconKey 'agenda' ainda não tem SVG dedicado
      // em sidebar-icons.tsx (fora de files_allowed deste slot) — cai no
      // fallback neutro do resolveIcon() até um slot de UI tocar aquele
      // arquivo.
      {
        href: '/agenda',
        label: 'Agenda',
        iconKey: 'agenda',
        permission: 'sales_appointments:read',
        featureFlag: 'sales.appointments.enabled',
      },
      {
        href: '/credit-analyses',
        label: 'Análise',
        iconKey: 'analise',
        permission: NAV_ACCESS.creditAnalyses,
      },
      {
        href: '/contratos',
        label: 'Contratos',
        iconKey: 'contratos',
        permission: NAV_ACCESS.contracts,
      },
      {
        href: '/conversas',
        label: 'Conversas',
        iconKey: 'conversas',
        permission: NAV_ACCESS.conversations,
      },
      // F56-S03: porta de entrada do pareamento do vendedor (antes era uma URL
      // solta que a gestora mandava). Visível só com a permissão E a flag; quem
      // usa apenas a caixa oficial (gestor geral, diretoria) não vê. iconKey
      // 'meu-whatsapp' ainda não tem SVG dedicado em sidebar-icons.tsx (fora de
      // files_allowed) — usa o fallback neutro do resolveIcon().
      {
        href: '/meu-whatsapp',
        label: 'Meu WhatsApp',
        iconKey: 'meu-whatsapp',
        permission: 'channel.self_pair',
        featureFlag: 'whatsapp_sync.pairing.enabled',
      },
      // Respostas rápidas NÃO fica na sidebar (decisão do Rogério 2026-07-23) —
      // é uma tela de administração, acessada pelo hub de Configurações
      // (ConfiguracoesPage.tsx). A rota /admin/quick-replies segue registrada.
    ],
  },
  {
    heading: 'Crédito',
    items: [
      {
        href: '/simulator',
        label: 'Simulador',
        iconKey: 'simulator',
        permission: NAV_ACCESS.simulator,
        featureFlag: 'credit_simulation.enabled',
      },
    ],
  },
  {
    heading: 'Gestão',
    items: [
      {
        href: '/relatorios',
        label: 'Relatórios',
        iconKey: 'relatorios',
        permission: NAV_ACCESS.reports,
      },
      // F35-S07 (doc 26 §6.5): item aparece só com sales_campaigns:read E a
      // flag do piloto comercial. iconKey 'campanhas' ainda não tem SVG
      // dedicado em sidebar-icons.tsx (fora de files_allowed deste slot) —
      // cai no fallback neutro do resolveIcon() até um slot de UI tocar
      // aquele arquivo (mesma situação de 'agenda', ver nota acima).
      {
        href: '/campanhas',
        label: 'Campanhas',
        iconKey: 'campanhas',
        permission: 'sales_campaigns:read',
        featureFlag: 'sales.campaigns.enabled',
      },
    ],
  },
];

/**
 * Primeira tela do menu que o usuário pode abrir, sem contar o Dashboard ("/").
 * Destino de quem cai em "/" sem acesso ao Dashboard (cobrança, diretoria…), para
 * o login não terminar numa tela de "sem acesso". Itens condicionados a feature
 * flag são ignorados (aqui só há permissão); o último recurso é a Central de Ajuda.
 */
export function firstAccessibleHref(hasPermission: (permission: string) => boolean): string {
  for (const section of APP_NAV) {
    for (const item of section.items) {
      if (item.href === '/' || item.featureFlag !== undefined) continue;
      if (item.permission === undefined || satisfiesAccess(item.permission, hasPermission)) {
        return item.href;
      }
    }
  }
  return '/ajuda';
}

/**
 * Navegação de rodapé (Configurações + Ajuda — isolados como padrão Linear/ClickUp).
 * Ordem: Ajuda primeiro (descobrível por novos usuários), Configurações depois.
 */
export const FOOTER_NAV: NavItem[] = [
  { href: '/ajuda', label: 'Ajuda', iconKey: 'help' },
  { href: '/configuracoes', label: 'Configurações', iconKey: 'configuracoes' },
];

// Nota F8-S18: TEMPLATES_NAV_ITEM e BILLING_NAV_ITEM foram removidos.
// Ambos eram dead code — nunca plugados em APP_NAV.
// O gating de cobrança e templates é feito diretamente em ConfiguracoesPage.tsx
// (grupo Gestão do AdminSection), com permissão + flag conforme billing/routes.ts
// e templates/routes.ts.

// Nota F12-S10: TUTORIAIS_NAV_ITEM removido (dead code do F12-S05).
// O gating de tutoriais é feito em ConfiguracoesPage.tsx (grupo Adm. técnica do AdminSection),
// com hasPermission('tutorials:manage') && flagEnabled('tutorials.enabled').
// A rota /admin/tutoriais está registrada em App.tsx (roteador real).
